import { CollapsibleSectionWithListener } from '@src/common/components/CollapsibleSectionWithListener';
import { formatWeightValueForDisplay } from '@src/common/components/FormatWeightValueForDisplay';
import { ReadonlyWeightInput } from '@src/common/components/ReadonlyWeightInput';
import { Stack } from '@src/common/components/Stack';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { parseIntOrUndefined } from '@src/common/utils/typeUtils';
import type { CollapseListener } from '@src/orders/CollapseListener';
import type { ComposedFormEntries } from '@src/orders/FormEntries';
import { AXLE_WEIGHT_DIFFERENCE_TOLERANCE_BETWEEN_REAL_AND_DISPLAYED_SUM } from '@src/orders/schemas/EditOrderFormInputs';
import {
  AxleFormName,
  AxleOverallWeightName,
  AxleWeightField,
  SumOfAxleWeightName,
} from '@src/permitField/AxleInformationFields';
import { OverallWeightField } from '@src/permitField/OverallDimensionsFields';
import { TractorAxlesFormUtils } from '@src/permitField/TractorAxlesFormUtils';
import { TrailersFormName } from '@src/permitField/TrailerInformationFields';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { calculateSumOfAxleWeights } from './AxleWeightCalculator';

const _fields = [AxleFormName];

//eslint-disable-next-line @typescript-eslint/no-unused-vars
const RelevantAxleFields = [AxleWeightField.fieldName];

type AllAxlesInformation = ComposedFormEntries<
  typeof _fields,
  typeof RelevantAxleFields
>;

type Props = CollapseListener;

export const AxleSummarySubSection = ({
  collapseEventName,
  uncollapseEventName,
}: Props) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext<AllAxlesInformation>();

  const [overallWeightFormValue, trailers, formTractorAxles] = useWatch({
    control,
    name: [
      OverallWeightField.fieldName,
      TrailersFormName,
      TractorAxlesFormUtils.pathToAxles,
    ],
  });

  const sumOfAxleWeight = calculateSumOfAxleWeights(trailers, formTractorAxles);

  useEffect(() => {
    if (
      Math.abs(
        (parseIntOrUndefined(
          formatWeightValueForDisplay(
            parseIntOrUndefined(overallWeightFormValue),
            false,
          ),
        ) || 0) -
          (parseIntOrUndefined(
            formatWeightValueForDisplay(sumOfAxleWeight, false),
          ) || 0),
      ) <= AXLE_WEIGHT_DIFFERENCE_TOLERANCE_BETWEEN_REAL_AND_DISPLAYED_SUM
    ) {
      clearErrors(AxleOverallWeightName);
    }
  }, [clearErrors, overallWeightFormValue, sumOfAxleWeight]);

  return (
    <CollapsibleSectionWithListener
      title={t('axleSummary')}
      id="axle-summary"
      collapseEventName={collapseEventName}
      uncollapseEventName={uncollapseEventName}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ReadonlyWeightInput
            className={fullWidth}
            value={sumOfAxleWeight}
            label={t(SumOfAxleWeightName)}
          />
          <ReadonlyWeightInput
            className={fullWidth}
            value={parseIntOrUndefined(overallWeightFormValue) ?? null}
            label={t(`fields.${AxleOverallWeightName}`)}
            error={
              errors[AxleOverallWeightName] &&
              t(`formErrors.${errors[AxleOverallWeightName]!.message}`)
            }
          />
        </Stack>
      </Stack>
    </CollapsibleSectionWithListener>
  );
};

const fullWidth = css({
  flex: 1,
});
